import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  expediaPrepaymentWarning,
  renderTerms,
  type CancellationPolicy,
  type CancellationTier,
  type RatePlan,
  type StoredCancellationPolicy,
} from '@lemontaigu/pms-core';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import { usePms } from './PmsProvider.js';
import { BankTransferSettings } from './BankTransferSettings.js';

// « Conditions » — the cancellation terms as RULES (PMS-PHONE-BOOKING-BRIEF P11–P15, §4.4).
//
// One page sets them and assigns each rate plan its policy; every reader (the
// call script, the confirmation, the website, the cancellation) renders from the
// same rules through pms-core's `renderTerms`, so a phrase is never typed twice.
// The text below each policy IS that renderer's output, not a copy of it.
//
// ── The OTA policies mirror the extranets (P15) ────────────────────────────
//
// Nothing here is pushed to an OTA. The operator confirms « l'extranet affiche
// ces conditions », which stamps `verifiedAt` with the server's clock; changing a
// rule clears it (the database does both). Gated by `pms.rate.write`: the desk
// applies terms, it does not set them.

type Draft = Omit<CancellationPolicy, 'verifiedAt'> & { id?: number };

const blank = (): Draft => ({
  code: '',
  labelFr: '',
  ota: false,
  prepaymentPct: 30,
  prepaymentAt: 'booking',
  tiers: [
    { moreThanDaysBefore: 5, retainPct: 30 },
    { moreThanDaysBefore: null, retainPct: 50 },
  ],
  noShowPct: 100,
});

const toDraft = (p: StoredCancellationPolicy): Draft => ({
  id: p.id,
  code: p.code,
  labelFr: p.labelFr,
  ota: p.ota,
  prepaymentPct: p.prepaymentPct,
  prepaymentAt: p.prepaymentAt,
  tiers: p.tiers.map((t) => ({ ...t })),
  noShowPct: p.noShowPct,
});

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

/** Whole number 0–max from what was typed, or null. */
const whole = (s: string, max: number): number | null => {
  const n = Number(s.trim());
  return Number.isInteger(n) && n >= 0 && n <= max ? n : null;
};

export function Conditions() {
  const pms = usePms();
  const canEdit = useCapability(CAPABILITIES.PMS_RATE_WRITE);
  const [policies, setPolicies] = useState<StoredCancellationPolicy[] | null>(null);
  const [plans, setPlans] = useState<RatePlan[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [ps, pl] = await Promise.all([pms.cancellationPolicies.policies(), pms.reference.ratePlans()]);
    if (!ps.ok) return setErr(ps.error.message);
    if (!pl.ok) return setErr(pl.error.message);
    setPolicies(ps.value);
    setPlans(pl.value);
  }, [pms]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (fn: () => Promise<{ ok: boolean; error?: { message: string } }>) => {
    setBusy(true);
    setErr('');
    const r = await fn();
    setBusy(false);
    if (!r.ok) {
      setErr(r.error?.message ?? 'Erreur');
      return false;
    }
    await load();
    return true;
  };

  const plansOf = (id: number) => plans.filter((p) => p.cancellationPolicyId === id);
  const byId = useMemo(() => new Map((policies ?? []).map((p) => [p.id, p])), [policies]);

  return (
    <div className="pms-conditions">
      <div className="pms-head">
        <h2 className="section-title">Conditions d’annulation</h2>
        <span className="pms-hint">
          Autant de politiques que nécessaire (haute saison, basse saison, période complète…) : chaque plan tarifaire en suit une
          par défaut, et la réception en choisit une à chaque réservation manuelle. Le texte lu au téléphone, la confirmation et le site
          sont rédigés à partir de ces règles. {canEdit ? '' : 'Lecture seule.'}
        </span>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {policies === null && !err && <p className="pms-empty">Chargement…</p>}
      {policies !== null && policies.length === 0 && draft === null && (
        <p className="pms-empty">Aucune politique pour cet établissement. Les plans ne sont vendus sous aucune condition écrite.</p>
      )}

      {(policies ?? []).map((p) => {
        const text = renderTerms(p, 'fr');
        const followers = plansOf(p.id);
        const warnExpedia = followers.some((pl) => expediaPrepaymentWarning(pl.channelCode ?? null, p));
        return (
          <section key={p.id} className="pms-form pms-policy">
            <h3>
              {p.labelFr} <span className="pms-sub">({p.code})</span>
            </h3>
            <p className="pms-sub">
              Plans : {followers.length === 0 ? 'aucun' : followers.map((pl) => pl.code).join(' · ')}
            </p>
            <ul>
              <li>{text.prepayment}</li>
              {text.cancellation.map((line) => (
                <li key={line}>{line}</li>
              ))}
              <li>{text.noShow}</li>
            </ul>
            {warnExpedia && (
              <p className="pms-hint" role="alert">
                ⚠ Un plan Expedia suit cette politique, dont les arrhes sont prises à la réservation : la carte virtuelle
                d’Expedia n’est active qu’à l’arrivée.
              </p>
            )}
            {p.ota && (
              <div className="pms-policy-sync">
                <strong>Synchronisation avec l’extranet</strong>{' '}
                {p.verifiedAt === null ? (
                  <span>— non vérifiée depuis la dernière modification.</span>
                ) : (
                  <span>— vérifiée le {fmtDate(p.verifiedAt)}.</span>
                )}{' '}
                {canEdit && (
                  <button
                    type="button"
                    className="pms-mini"
                    disabled={busy}
                    onClick={() => void run(() => pms.cancellationPolicies.verifyPolicy(p.id))}
                  >
                    L’extranet affiche ces conditions
                  </button>
                )}
              </div>
            )}
            {canEdit && draft === null && (
              <div className="pms-form-actions">
                <button type="button" className="pms-mini" onClick={() => setDraft(toDraft(p))}>
                  Modifier
                </button>
                {followers.length === 0 && (
                  <button
                    type="button"
                    className="pms-mini"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm(`Supprimer « ${p.labelFr} » ?`)) void run(() => pms.cancellationPolicies.deletePolicy(p.id));
                    }}
                  >
                    Supprimer
                  </button>
                )}
              </div>
            )}
          </section>
        );
      })}

      {canEdit && draft === null && (
        <div className="pms-room-add">
          <button type="button" className="auth-btn is-ghost" onClick={() => setDraft(blank())}>
            + Nouvelle politique
          </button>
        </div>
      )}

      {draft !== null && (
        <PolicyEditor
          draft={draft}
          busy={busy}
          onChange={setDraft}
          onCancel={() => {
            setDraft(null);
            setErr('');
          }}
          onSave={async () => {
            if (await run(() => pms.cancellationPolicies.savePolicy(draft))) setDraft(null);
          }}
        />
      )}

      {plans.length > 0 && policies !== null && (
        <section className="pms-form">
          <h3>Plans tarifaires</h3>
          <table className="pms-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Libellé</th>
                <th>Politique</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((pl) => (
                <tr key={pl.id}>
                  <td>{pl.code}</td>
                  <td>{pl.labelFr}</td>
                  <td>
                    {canEdit ? (
                      <select
                        aria-label={`Politique du plan ${pl.code}`}
                        value={pl.cancellationPolicyId ?? ''}
                        disabled={busy}
                        onChange={(e) =>
                          void run(() =>
                            pms.cancellationPolicies.assignPlanPolicy(pl.id, e.target.value === '' ? null : Number(e.target.value)),
                          )
                        }
                      >
                        <option value="">— aucune —</option>
                        {policies.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.labelFr}
                          </option>
                        ))}
                      </select>
                    ) : (
                      (byId.get(pl.cancellationPolicyId ?? -1)?.labelFr ?? '—')
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <BankTransferSettings canEdit={canEdit} />
    </div>
  );
}

interface EditorProps {
  draft: Draft;
  busy: boolean;
  onChange: (d: Draft) => void;
  onSave: () => void;
  onCancel: () => void;
}

/** The form, with the text and a sample stay's dates and amounts rendered live. */
function PolicyEditor({ draft, busy, onChange, onSave, onCancel }: EditorProps) {
  const [arrival, setArrival] = useState('2026-12-04');
  const [total, setTotal] = useState('208,20');
  const set = (patch: Partial<Draft>) => onChange({ ...draft, ...patch });
  const setTier = (i: number, patch: Partial<CancellationTier>) =>
    set({ tiers: draft.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });

  const totalCents = Math.round(Number(total.replace(',', '.')) * 100);
  const sample =
    /^\d{4}-\d{2}-\d{2}$/.test(arrival) && Number.isFinite(totalCents) && totalCents >= 0
      ? { arrival, totalCents }
      : undefined;
  const policy: CancellationPolicy = { ...draft, verifiedAt: null };

  return (
    <section className="pms-form">
      <h3>{draft.id === undefined ? 'Nouvelle politique' : `Modifier « ${draft.labelFr} »`}</h3>
      {draft.ota && draft.id !== undefined && (
        <p className="pms-hint">
          Politique OTA : l’extranet fait foi. Modifiez d’abord l’extranet, puis reportez ici — la vérification sera à
          refaire.
        </p>
      )}
      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Code *</span>
          <input
            value={draft.code}
            disabled={draft.id !== undefined}
            placeholder="DIRECT"
            onChange={(e) => set({ code: e.target.value.toUpperCase() })}
          />
        </label>
        <label className="pms-field pms-field-wide">
          <span>Libellé *</span>
          <input value={draft.labelFr} onChange={(e) => set({ labelFr: e.target.value })} />
        </label>
        <label className="pms-field">
          <span>Miroir d’un extranet (OTA)</span>
          <input type="checkbox" checked={draft.ota} onChange={(e) => set({ ota: e.target.checked })} />
        </label>
        <label className="pms-field">
          <span>Arrhes (%)</span>
          <input
            inputMode="numeric"
            defaultValue={String(draft.prepaymentPct)}
            onBlur={(e) => {
              const n = whole(e.target.value, 100);
              if (n !== null) set({ prepaymentPct: n });
            }}
          />
        </label>
        <label className="pms-field">
          <span>Arrhes prises</span>
          <select
            value={draft.prepaymentAt}
            onChange={(e) => set({ prepaymentAt: e.target.value as Draft['prepaymentAt'] })}
          >
            <option value="booking">à la réservation</option>
            <option value="checkin">à l’arrivée</option>
          </select>
        </label>
        <label className="pms-field">
          <span>Non-présentation (%)</span>
          <input
            inputMode="numeric"
            defaultValue={String(draft.noShowPct)}
            onBlur={(e) => {
              const n = whole(e.target.value, 100);
              if (n !== null) set({ noShowPct: n });
            }}
          />
        </label>
      </div>

      <h4>Annulation</h4>
      <table className="pms-table">
        <thead>
          <tr>
            <th>Plus de … jours avant l’arrivée</th>
            <th>Montant retenu (%)</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {draft.tiers.map((t, i) => {
            const last = i === draft.tiers.length - 1;
            return (
              <tr key={i}>
                <td>
                  {last ? (
                    'jusqu’à l’arrivée'
                  ) : (
                    <input
                      aria-label={`Jours, palier ${i + 1}`}
                      inputMode="numeric"
                      defaultValue={String(t.moreThanDaysBefore)}
                      onBlur={(e) => {
                        const n = whole(e.target.value, 365);
                        if (n !== null && n >= 1) setTier(i, { moreThanDaysBefore: n });
                      }}
                    />
                  )}
                </td>
                <td>
                  <input
                    aria-label={`Pourcentage, palier ${i + 1}`}
                    inputMode="numeric"
                    defaultValue={String(t.retainPct)}
                    onBlur={(e) => {
                      const n = whole(e.target.value, 100);
                      if (n !== null) setTier(i, { retainPct: n });
                    }}
                  />
                </td>
                <td>
                  {!last && (
                    <button type="button" className="pms-mini" onClick={() => set({ tiers: draft.tiers.filter((_, j) => j !== i) })}>
                      Retirer
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="pms-mini"
        onClick={() => {
          // A new tier goes first, before the earliest existing threshold.
          const earliest = draft.tiers[0]?.moreThanDaysBefore ?? 0;
          set({ tiers: [{ moreThanDaysBefore: earliest + 7, retainPct: 0 }, ...draft.tiers] });
        }}
      >
        + Palier
      </button>

      <h4>Texte</h4>
      <TermsPreview title="Français" policy={policy} lang="fr" />
      <TermsPreview title="English" policy={policy} lang="en" />

      <h4>Exemple de séjour</h4>
      <div className="pms-form-grid">
        <label className="pms-field">
          <span>Arrivée</span>
          <input type="date" value={arrival} onChange={(e) => setArrival(e.target.value)} />
        </label>
        <label className="pms-field">
          <span>Montant total (€)</span>
          <input inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} />
        </label>
      </div>
      {sample && <TermsPreview title="Pour ce séjour" policy={policy} lang="fr" stay={sample} />}

      <div className="pms-form-actions">
        <button type="button" className="auth-btn is-ghost" onClick={onCancel} disabled={busy}>
          Annuler
        </button>
        <button type="button" className="auth-btn" onClick={onSave} disabled={busy}>
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </section>
  );
}

function TermsPreview({
  title,
  policy,
  lang,
  stay,
}: {
  title: string;
  policy: CancellationPolicy;
  lang: 'fr' | 'en';
  stay?: { arrival: string; totalCents: number };
}) {
  // A scale mid-edit can be momentarily invalid (a tier out of order): the
  // renderer still answers, and the save is what refuses it.
  const t = renderTerms(policy, lang, stay);
  return (
    <div className="pms-policy-preview">
      <strong>{title}</strong>
      <ul>
        <li>{t.prepayment}</li>
        {t.cancellation.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
        <li>{t.noShow}</li>
        <li className="pms-sub">{t.basis}</li>
      </ul>
    </div>
  );
}
